import jwtDecode from 'jwt-decode'
import { Buffer } from 'buffer'
import CryptoJS from 'crypto-js'
import { app } from '../../config'
import { REMBLE_API_URL } from 'components/common/api'

const REMBLE_OAUTH_STATE_KEY = 'remble_oauth_state'
const REMBLE_OAUTH_ID_TOKEN_KEY = 'remble_oauth_id_token'
const REMBLE_OAUTH_DOMAIN = 'remble_oauth_domain'
const REMBLE_AUTH_DATA_KEY = 'remble_auth_data'
const REMBLE_AUTH_STORAGE_KEY = 'remble_auth_storage_key'
const REMBLE_VERIFICATION_KEY = 'remble_verification_data'
const REMBLE_PASSWORD_MIN_LENGTH = 8

// TODO: get key based on user client
const _getAuthKey = () => '' + app.ios.version // must be a string

/**
 * Retrieves the authenticated user stored in a JWT token, if no param is passed, it defaults to the one stored
 * in local storage
 * @param {String} token The JWT token to decode. If a token is not passed, then the one stored in local storage is used
 */
const getAuthenticatedUser = (token = null) => {
  // Enable passing a token to the function to support geting the user out of a token payload
  if (!token) token = getAuthenticatedUserToken()

  // No token, no user
  if (!token) return null

  const decoded = jwtDecode(token)

  return decoded.data.user
}

/**
 * Retrieves the currently authenticated users JWT token stored in localStorage
 */
const getAuthenticatedUserToken = () => {
  const key = _getAuthKey()
  const encodedToken = localStorage.getItem(REMBLE_AUTH_DATA_KEY)

  // No token, no user
  let decryptedData

  if (encodedToken) {
    try {
      decryptedData = decryptData(key, encodedToken)
    } catch (error) {} // Decrypt failed
  }

  return decryptedData
}

/**
 * Determines whether or not the passed in token is a valid token via simple validation
 * Server-side validation happens any time the token is passed to the back-end
 * @param {String} token The token to validate
 */
const isValidToken = token => {
  let isValid = false

  try {
    // Return early if we didn't receive a token
    if (!token) return null

    const decoded = jwtDecode(token)

    isValid =
      Date.now() <= decoded.exp * 1000 && // Make sure token is not expired
      decoded.iss === app.wp_api.url // Make sure issuing server is our host
  } catch (error) {
    // If we failed parsing or decoding, the token is invalid
    console.error('jwt decode error', error)
  }

  return isValid
}

/**
 * Determines whether or not the current user is authenticated and has a valid stored token
 */
const isAuthenticated = () => {
  let isValid = false

  // Try to get the token from local storage
  try {
    const token = getAuthenticatedUserToken()
    isValid = token && isValidToken(token)
  } catch (error) {
    console.error('isAuthenticated error', error)
  }

  return isValid
}

/**
 * Accepts a string and a secret key to use to encrypt with, encrypts the data and returns it
 * @param {String} key The secret key to use to encrypt the data string
 * @param {Object} data The data string to encrypt
 * @returns An encrypted string
 */
const encryptData = (key, data) => {
  const b64EncodedToken = Buffer.from(data).toString('base64')
  return CryptoJS.AES.encrypt(b64EncodedToken, key).toString()
}

/**
 * Accepts encrypted data string and the secret key to use to decrypt the string
 * @param {String} key The secret key to use to decrypt the passed in auth token
 * @param {Object} encryptedData The encoded auth token to decode
 * @returns A decrypted string, if successful
 */
const decryptData = (key, encryptedData) => {
  // Return early if we didn't receive a key or data
  if (!key || !encryptedData) return null

  const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, key)
  const data = decryptedBytes.toString(CryptoJS.enc.Utf8)
  return Buffer.from(data, 'base64').toString('utf8')
}

/**
 * Accepts verification data object and a secret key to use to encrypt it with and returns the encrypted result
 * @param {String} key The secret key to use to encrypt the data
 * @param {Object} data The verification data to store
 */
const encryptVerificationData = (key, data) =>
  encryptData(key, JSON.stringify(data))

/**
 * Accepts encrypted verification data string and the secret key to use to decrypt it with
 * @param {String} key The secret key to use to decrypt the verification data
 * @param {Object} encryptedData The encrypted verification data to decrypt
 */
const decryptVerificationData = (key, encryptedData) =>
  JSON.parse(decryptData(key, encryptedData))

/**
 * "Logs in" the current user by storing the passed in `key` and auth `data` in localStorage
 * IMPORTANT NOTE: The mobile client watches for changes to the auth data in localStorage and will
 * automatically dimiss the login screen if the auth data is set
 * @param {Object} data The JWT auth data to store
 */
const login = data => {
  const key = _getAuthKey()
  const encryptedData = encryptData(key, data.token)
  localStorage.setItem(REMBLE_AUTH_DATA_KEY, encryptedData)
}

/**
 * "Logs out" the current user by clearing the stored JWT Auth data
 */
const logout = () => {
  localStorage.removeItem(REMBLE_AUTH_DATA_KEY)

  // Clear out all data
  clearVerificationData()
}

/**
 * Helper function to set the local storage key for the current user.
 * NOTE: Setting this key will cause the native mobile app to automatically log the user in
 * @param {String} key The key to store
 */
const setLocalAuthKey = key => {
  localStorage.setItem(REMBLE_AUTH_STORAGE_KEY, key)
}

/**
 * Clears previously stored account verification data
 */
const clearVerificationData = () => {
  localStorage.removeItem(REMBLE_VERIFICATION_KEY)
}

/**
 * Fetches previously stored account verification data
 */
const fetchVerificationData = () => {
  let verificationData

  // Try to get the encrypted data from local storage
  try {
    const key = _getAuthKey()
    const encryptedData = localStorage.getItem(REMBLE_VERIFICATION_KEY)
    verificationData = decryptVerificationData(key, encryptedData)
  } catch (error) {
    console.error('fetchVerificationData error', error)
  }

  return verificationData
}

/**
 * Stores data necessary for account verification
 * @param {Object} data The verification data to store
 */
const storeVerificationData = data => {
  const key = _getAuthKey()
  const encryptedData = encryptVerificationData(key, data)
  localStorage.setItem(REMBLE_VERIFICATION_KEY, encryptedData)
}

/**
 * Gets the Apple OAuth login URL
 */
const getAppleLoginUrl = async () => {
  const url = `${REMBLE_API_URL}/users/login/apple`
  const result = await fetch(url)
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  return result
}

/**
 * Gets the Google OAuth login URL
 */
const getGoogleLoginUrl = async () => {
  const url = `${REMBLE_API_URL}/users/login/google`
  const result = await fetch(url)
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  return result
}

/**
 * Gets the OpenID OAuth login URL
 */
const getOpenIDLoginUrl = async domain => {
  const url = `${REMBLE_API_URL}/users/login/oauth?user_domain=${domain}&subdomain=app`
  const result = await fetch(url)
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  return result
}

/**
 * Completes the Apple OAuth login flow
 * @param {String} code The code to use to complete the OAuth flow
 * @param {String} state The state to use to complete the OAuth flow
 * @param {String} id_token (Optional) The id_token to use to complete the OAuth flow. This is only used for native apps
 * @param {String} user_domain The domain to which the user belongs
 */
const completeAppleLoginWithCodeAndState = async (
  code,
  state,
  id_token = '',
  user_domain = ''
) =>
  completeOAuthLoginWithCodeAndState(
    `${REMBLE_API_URL}/users/login/apple/complete`,
    code,
    state,
    id_token,
    user_domain
  )

/**
 * Completes the Google OAuth login flow
 * @param {String} code The code to use to complete the OAuth flow
 * @param {String} state The state to use to complete the OAuth flow
 * @param {String} id_token (Optional) The id_token to use to complete the OAuth flow. This is only used for native apps
 * @param {String} user_domain The domain to which the user belongs
 */
const completeGoogleLoginWithCodeAndState = async (
  code,
  state,
  id_token = '',
  user_domain = ''
) =>
  completeOAuthLoginWithCodeAndState(
    `${REMBLE_API_URL}/users/login/google/complete`,
    code,
    state,
    id_token,
    user_domain
  )

/**
 * Completes the OpenID OAuth login flow
 * @param {String} code The code to use to complete the OAuth flow
 * @param {String} state The state to use to complete the OAuth flow
 * @param {String} id_token (Optional) The id_token to use to complete the OAuth flow. This is only used for native apps
 * @param {String} user_domain The domain to which the user belongs
 */
const completeOpenIDLoginWithCodeAndState = async (
  code,
  state,
  id_token = '',
  user_domain = ''
) =>
  completeOAuthLoginWithCodeAndState(
    `${REMBLE_API_URL}/users/login/oauth/complete`,
    code,
    state,
    id_token,
    user_domain
  )

/**
 * Completes an OAuth login flow
 * @param {String} url The url to use to complete the OAuth flow
 * @param {String} code The code to use to complete the OAuth flow
 * @param {String} state The state to use to complete the OAuth flow
 * @param {String} id_token (Optional) The id_token to use to complete the OAuth flow. This is only used for native apps
 * @param {String} user_domain (Optional) The domain to which the user belongs
 */
const completeOAuthLoginWithCodeAndState = async (
  url,
  code,
  state,
  id_token = '',
  user_domain = ''
) => {
  const result = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ code, state, id_token, user_domain }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  return result
}

export {
  logout,
  login,
  isAuthenticated,
  isValidToken,
  clearVerificationData,
  fetchVerificationData,
  getAuthenticatedUserToken,
  getAuthenticatedUser,
  getAppleLoginUrl,
  getGoogleLoginUrl,
  getOpenIDLoginUrl,
  completeAppleLoginWithCodeAndState,
  completeGoogleLoginWithCodeAndState,
  completeOpenIDLoginWithCodeAndState,
  storeVerificationData,
  setLocalAuthKey,
  REMBLE_PASSWORD_MIN_LENGTH,
  REMBLE_OAUTH_STATE_KEY,
  REMBLE_OAUTH_ID_TOKEN_KEY,
  REMBLE_OAUTH_DOMAIN
}
